import { useEffect, useState } from "react";
import axios from "axios";
import {
  Factory, HardHat, Flame, Truck, Stethoscope, GraduationCap,
  Building2, Bolt, Briefcase, ShieldAlert, Warehouse, Users,
  ToggleLeft, ToggleRight, Plus, Pencil, Trash2,
} from "lucide-react";
import { authHeader } from "@/hooks/useAdminAuth";
import API from "@/lib/api";
import AdminFormModal, { Field, TextInput, NumberInput, TextArea, Select } from "@/components/admin/AdminFormModal";

const ICONS = {
  Factory, HardHat, Flame, Truck, Stethoscope, GraduationCap,
  Building2, Bolt, Briefcase, ShieldAlert, Warehouse, Users,
};
const ICON_KEYS = Object.keys(ICONS);

const EMPTY = {
  name: "",
  desc: "",
  icon: "Factory",
  sort_order: 0,
  is_active: true,
};

export default function AdminIndustries() {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/industries?admin=true`)
      .then(({ data }) => setIndustries(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, sort_order: industries.length });
    setModalOpen(true);
  };

  const openEdit = (ind) => {
    setEditing(ind);
    setForm({
      name: ind.name || "",
      desc: ind.desc || "",
      icon: ind.icon || "Factory",
      sort_order: ind.sort_order ?? 0,
      is_active: ind.is_active ?? true,
    });
    setModalOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
      if (editing) {
        await axios.put(`${API}/industries/${editing.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/industries`, payload, { headers: authHeader() });
      }
      setModalOpen(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, current) => {
    try {
      await axios.put(`${API}/industries/${id}`, { is_active: !current }, { headers: authHeader() });
      setIndustries((is) => is.map((i) => (i.id === id ? { ...i, is_active: !current } : i)));
    } catch (err) {
      console.error(err);
    }
  };

  const remove = async (ind) => {
    if (!window.confirm(`Delete industry "${ind.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/industries/${ind.id}`, { headers: authHeader() });
      setIndustries((is) => is.filter((x) => x.id !== ind.id));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7B2CBF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-display">Industries</h1>
          <p className="text-gray-500 mt-1 text-sm">{industries.length} sectors</p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-[#00A8E8] to-[#7B2CBF] text-white hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" /> New Industry
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {industries.map((ind) => {
          const Icon = ICONS[ind.icon] || Factory;
          return (
            <div
              key={ind.id}
              className={`bg-[#0D0E13] border border-white/5 rounded-xl p-4 flex items-start gap-3 transition-opacity ${
                ind.is_active ? "" : "opacity-50"
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-[#00A8E8]/15 border border-[#00A8E8]/25 flex items-center justify-center shrink-0 mt-0.5">
                <Icon className="w-4 h-4 text-[#00A8E8]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-white text-sm font-medium">{ind.name}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => toggleActive(ind.id, ind.is_active)}
                      className="text-gray-500 hover:text-white transition-colors"
                      title={ind.is_active ? "Hide" : "Show"}
                    >
                      {ind.is_active ? (
                        <ToggleRight className="w-5 h-5 text-green-400" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => openEdit(ind)}
                      className="text-gray-500 hover:text-[#00A8E8] transition-colors p-0.5"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(ind)}
                      className="text-gray-500 hover:text-red-400 transition-colors p-0.5"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-0.5 leading-relaxed line-clamp-2">{ind.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      <AdminFormModal
        open={modalOpen}
        title={editing ? "Edit Industry" : "New Industry"}
        onClose={() => setModalOpen(false)}
        onSubmit={submit}
        submitting={saving}
        submitLabel={editing ? "Update Industry" : "Create Industry"}
      >
        <Field label="Name" required>
          <TextInput value={form.name} onChange={(e) => update("name", e.target.value)} required />
        </Field>
        <Field label="Description" required>
          <TextArea value={form.desc} onChange={(e) => update("desc", e.target.value)} required />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Icon">
            <Select value={form.icon} onChange={(e) => update("icon", e.target.value)}>
              {ICON_KEYS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </Select>
          </Field>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => update("is_active", e.target.checked)}
            className="w-4 h-4 accent-[#7B2CBF]"
          />
          <span className="text-sm text-white">Active (visible on site)</span>
        </label>
      </AdminFormModal>
    </div>
  );
}
